import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { flex, height, overflow, position, space, width } from 'styled-system';

import { Flex } from 'common/components/Flex';
import { Grid, GridItem } from 'common/components/Grid';
import { ResponsiveLazyImage } from 'common/components/LazyImage';
import type { ResponsiveLazyImageProps } from 'common/components/LazyImage/types';
import { HeadlineH5 } from 'common/components/Typography';
import { textWithEllipsis } from 'common/theme/styles';
import { textDecoration, textTransform, transition } from 'common/theme/system-utilities';
import Content from './Content';

interface SmallImage {
  image: ResponsiveLazyImageProps;
  headline: string;
  subtitle: string;
  url: string;
}

export interface TopNewsSectionProps {
  largeImage: ResponsiveLazyImageProps;
  headlineLargeImage: string;
  subtitleLargeImage: string;
  urlLargeImage: string;
  headlineSmallImages: string;
  smallImages: SmallImage[];
}

const SMALL_IMAGE_MAX_WIDTH = ['136px', '128px', null, '146px'];
const SMALL_IMAGE_HEIGHT = ['91px', '86px', null, '97px'];
const PADDING_X = ['sp_0', null, 'sp_24', 'sp_32'];
const GRID_ITEM_STYLED_PROPS = {
  w: [1, null, 4 / 12],
  flex: ['1 0 100%', null, '1 0 280px', '1 0 400px'],
  pl: ['sp_16', 'sp_24', 'sp_0'],
  pr: ['sp_16', 'sp_24', null, 'sp_32'],
  pt: ['sp_24', null, 'sp_0'],
};
const LARGE_CONTENT_STYLED_PROPS = {
  border: '1px solid #e2e4e4',
};
const SMALL_CONTENT_STYLED_PROPS = {
  pr: 'sp_0',
  py: 'sp_0',
  border: 'none',
};

const StyledGridItem = styled.div<any>`
  ${flex};
  ${space};
  ${width};
`;

const gridItemStyles: ComponentProps<typeof StyledGridItem> = {
  as: GridItem,
  w: [1, null, 8 / 12],
};

const StyledLink = styled(Flex)<any>`
  ${height};
  ${textDecoration};
  ${transition};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const linkStyles: ComponentProps<typeof StyledLink> = {
  as: 'a',
  height: 'auto',
  textDecoration: 'none',
  transition: 'all 0.6s ease',
};

const ImageWrapper = styled.div<any>`
  ${position};
  ${overflow};
  ${space};
`;

const imageWrapperStyles: ComponentProps<typeof ImageWrapper> = {
  position: 'relative',
  overflow: 'hidden',
  pt: '50%',
};

const Image = styled(ResponsiveLazyImage)<any>`
  ${width};
  ${height};
  ${flex};
  ${position};
  top: 0;
  left: 0;
`;

const imageStyles: ComponentProps<typeof Image> = {
  width: 1,
  height: '100%',
  flex: 'none',
};

const StyledHeadline = styled(HeadlineH5)<any>`
  ${textTransform};
  ${textWithEllipsis(1)};
  ${space};
`;

const headlineStyles: ComponentProps<typeof StyledHeadline> = {
  textTransform: 'none',
  mb: ['sp_16', null, null, 'sp_24'],
};

const TopNewsSection = ({
  largeImage,
  headlineLargeImage,
  subtitleLargeImage,
  urlLargeImage,
  headlineSmallImages,
  smallImages,
}: TopNewsSectionProps) => (
  <Grid data-corgi-id="top-news-section" flexDirection={['column', null, 'row']} px={PADDING_X}>
    <StyledGridItem {...gridItemStyles} px={PADDING_X}>
      <StyledLink {...linkStyles} flexDirection="column" href={urlLargeImage}>
        <ImageWrapper {...imageWrapperStyles}>
          <Image {...imageStyles} {...largeImage} position="absolute" />
        </ImageWrapper>
        <Content
          headline={headlineLargeImage}
          subtitle={subtitleLargeImage}
          {...LARGE_CONTENT_STYLED_PROPS}
        />
      </StyledLink>
    </StyledGridItem>
    <StyledGridItem {...gridItemStyles} {...GRID_ITEM_STYLED_PROPS}>
      <StyledHeadline {...headlineStyles}>{headlineSmallImages}</StyledHeadline>
      {smallImages.map(({ image, headline, subtitle, url }, index) => (
        <StyledLink
          {...linkStyles}
          key={`small-image-${index}`}
          href={url}
          height={SMALL_IMAGE_HEIGHT}
          mb={['sp_16', null, 'sp_24', 'sp_32']}
        >
          <Image {...imageStyles} {...image} width={SMALL_IMAGE_MAX_WIDTH} />
          <Content headline={headline} subtitle={subtitle} {...SMALL_CONTENT_STYLED_PROPS} />
        </StyledLink>
      ))}
    </StyledGridItem>
  </Grid>
);

export default TopNewsSection;
